'use strict';

// ███ S1 — PAY FIELDS LIVE IN DOCUMENTS OF THEIR OWN ███ (brief-security-pass, 2026-10-30)
//
// Firestore rules decide per DOCUMENT, never per field, so "an employee may not
// read pay" means pay lives in documents the rules can gate. This module is the
// ONE place that says which fields are pay, where they live, and how a document
// is split into its pay half and its everything-else half. Rules tests, the
// Cloud Functions, the two migration scripts and the client all derive from it
// (CLAUDE.md § 4: two predicates partitioning one concept must share a source).
//
// THE THREE HOMES
//   organisations/{orgId}/people/{personId}/private/wage              — wage settings
//   organisations/{orgId}/people/{personId}/private/wage/history/{id} — wage history
//   organisations/{orgId}/shiftCosts/{shiftId}                        — a shift's cost
//   organisations/{orgId}/timesheetCosts/{timesheetId}                — a timesheet's cost
//
// Readable by the PAY TIERS only (Supervisor, Manager, Systems Administrator —
// `PAY_TIERS` below, the same list `src/composables/usePayVisibility.js` renders
// from). An employee never reads a cost from the database: with
// `showPayToEmployees` on, the Dashboard asks `ownPayFigures` and the function
// checks the setting server-side (ruling 3). The rules hold no `get()` of that
// setting (ruling 5).
//
// Stated NEGATIVELY, because a reader supplies their own completion for a
// positive definition: a shift or timesheet document carries NO `cost`, NO
// `costBreakdown`, NO `costFlags`; a person document carries NO wage field.
// During the migration window (prod steps 1–5) the main documents STILL carry
// them — `PAY_FIELDS_MODE=dual` in functions — and the strip (step 6) is what
// makes the negative sentence true.

// The nine wage fields. Same list as `functions/personUpdateGuard.js ::
// WAGE_FIELDS_BLOCKED_ON_GENERIC_UPDATE` and the people self-update blocklist in
// `firestore.rules`; `src/tests/payDocsContract.test.js` asserts the three agree.
const WAGE_FIELDS = Object.freeze([
  'wageConfig',
  'weekdayRate',
  'saturdayRate',
  'sundayRate',
  'saturdayRateType',
  'sundayRateType',
  'appliedWageProfileId',
  'appliedWageProfileName',
  'wageEffectiveDate',
]);

// The pay keys a shift or timesheet document must not carry after the strip.
const PAY_KEYS = Object.freeze(['cost', 'costBreakdown', 'costFlags']);

const PAY_TIERS = Object.freeze(['Supervisor', 'Manager', 'Systems Administrator']);

const WAGE_DOC_ID = 'wage';
const PRIVATE_SUBCOLLECTION = 'private';
const WAGE_HISTORY_SUBCOLLECTION = 'history';
const SHIFT_COSTS = 'shiftCosts';
const TIMESHEET_COSTS = 'timesheetCosts';

function isPayTier(permissionName) {
  return typeof permissionName === 'string' && PAY_TIERS.includes(permissionName);
}

function wageDocPath(orgId, personId) {
  return `organisations/${orgId}/people/${personId}/${PRIVATE_SUBCOLLECTION}/${WAGE_DOC_ID}`;
}
function wageHistoryPath(orgId, personId) {
  return `${wageDocPath(orgId, personId)}/${WAGE_HISTORY_SUBCOLLECTION}`;
}
function shiftCostsPath(orgId) {
  return `organisations/${orgId}/${SHIFT_COSTS}`;
}
function timesheetCostsPath(orgId) {
  return `organisations/${orgId}/${TIMESHEET_COSTS}`;
}

const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

/** Split a person document into { wage, rest }. `wage` holds only the keys present. */
function splitWageFields(person) {
  const wage = {};
  const rest = {};
  for (const [k, v] of Object.entries(person || {})) {
    if (WAGE_FIELDS.includes(k)) wage[k] = v; else rest[k] = v;
  }
  return { wage, rest };
}

/** True when the person document carries ANY wage field (the strip's "still to do" predicate). */
function personCarriesWage(person) {
  return WAGE_FIELDS.some((k) => person && hasOwn(person, k));
}

/**
 * The pricing engine (`shared/wage-engine`) reads `person.wageConfig` and the
 * legacy rate fields off ONE object. After S1 those live in the wage document,
 * so every pricing caller merges them back on: `withWage(person, wageDoc)`.
 * A missing wage document yields the person unchanged — which prices at $0 and
 * sets `costFlags.personMissing`-class flags exactly as a person with no
 * `wageConfig` did before. Nothing here invents a rate.
 */
function withWage(person, wageDoc) {
  if (!person) return person;
  const { rest } = splitWageFields(person);
  return { ...rest, ...(wageDoc && typeof wageDoc === 'object' ? splitWageFields(wageDoc).wage : {}) };
}

/** Split a shift/timesheet document into { pay, rest }. */
function splitPayKeys(docData) {
  const pay = {};
  const rest = {};
  for (const [k, v] of Object.entries(docData || {})) {
    if (PAY_KEYS.includes(k)) pay[k] = v; else rest[k] = v;
  }
  return { pay, rest };
}

function docCarriesPay(docData) {
  return PAY_KEYS.some((k) => docData && hasOwn(docData, k));
}

// ── The side-document shapes ────────────────────────────────────────────────
//
// Each cost document carries `date` (the ISO start date, `YYYY-MM-DD`) and
// `locationId`, so a Calendar week or a report period is ONE range query
// (ruling 4), plus `employeeId` so an own-row lookup is one equality. `date` is
// `startDateISO` where the main document has it (`shared/shift-dates` derives
// it), else derived here from the `DD/MM/YYYY` `startDate` — the same rule
// `deriveDateIsoUpdate` applies, written out so this module has no dependency.
function isoFromDdMmYyyy(v) {
  const m = typeof v === 'string' ? v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) : null;
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  if (d.getUTCFullYear() !== Number(yyyy) || d.getUTCMonth() !== Number(mm) - 1 || d.getUTCDate() !== Number(dd)) return null;
  return `${yyyy}-${mm}-${dd}`;
}

function dateOf(mainDoc) {
  if (typeof mainDoc?.startDateISO === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(mainDoc.startDateISO)) return mainDoc.startDateISO;
  return isoFromDdMmYyyy(mainDoc?.startDate);
}

const nullIfUndefined = (v) => (v === undefined ? null : v);

/**
 * Build the `shiftCosts/{shiftId}` document from a shift document (or the
 * pay fields a writer is about to put on one). Returns null when the inputs
 * cannot name a date — a cost document with no `date` is invisible to every
 * range query, so it is refused rather than written blind.
 */
function shiftCostDoc({ orgId, shiftId, shift, pay }) {
  const p = pay || splitPayKeys(shift).pay;
  const date = dateOf(shift);
  if (!orgId || !shiftId || !date) return null;
  return {
    organisationId: orgId,
    shiftId,
    employeeId: nullIfUndefined(shift?.employeeId ?? null),
    locationId: nullIfUndefined(shift?.locationId ?? null),
    date,
    // R2 (2026-10-31): blank is not zero — see timesheetCostDoc.
    ...(typeof p.cost === 'string' ? { cost: p.cost } : {}),
    costFlags: p.costFlags && typeof p.costFlags === 'object' ? p.costFlags : {},
  };
}

function timesheetCostDoc({ orgId, timesheetId, timesheet, pay }) {
  const p = pay || splitPayKeys(timesheet).pay;
  const date = dateOf(timesheet);
  if (!orgId || !timesheetId || !date) return null;
  return {
    organisationId: orgId,
    timesheetId,
    employeeId: nullIfUndefined(timesheet?.employeeId ?? null),
    locationId: nullIfUndefined(timesheet?.locationId ?? null),
    date,
    // R2 (2026-10-31): blank is not zero — a main document with no string cost
    // yields a side document with NO cost key (the migration counts it NO-COST),
    // never a minted "0.00" a reader cannot tell from a real zero.
    ...(typeof p.cost === 'string' ? { cost: p.cost } : {}),
    ...(Array.isArray(p.costBreakdown) ? { costBreakdown: p.costBreakdown } : {}),
    costFlags: p.costFlags && typeof p.costFlags === 'object' ? p.costFlags : {},
  };
}

/**
 * Person-history entries written by `updatePersonWages` / `bulkApplyWageProfile`
 * carry `{ field, oldValue, newValue }` for wage fields. The person document
 * keeps no rate (ruling 2): the wage changes move to the wage history
 * subcollection, and the entry left on the person says only THAT a wage
 * changed. `splitHistoryEntry` is used by the writers going forward and by the
 * migration for existing entries.
 */
// A history change names its field either whole (`wageConfig`) or by leaf
// (`wageConfig.baseRate`, as `functions/wageUpdateHistoryChanges.js ::
// buildWageUpdateHistoryChanges` writes it); both are wage changes.
function isWageChange(change) {
  if (!change || typeof change !== 'object' || typeof change.field !== 'string') return false;
  return WAGE_FIELDS.some((f) => change.field === f || change.field.startsWith(`${f}.`));
}

function splitHistoryEntry(entry) {
  if (!entry || typeof entry !== 'object' || !Array.isArray(entry.changes)) {
    return { personEntry: entry, wageEntry: null, moved: 0 };
  }
  const wageChanges = entry.changes.filter(isWageChange);
  if (wageChanges.length === 0) return { personEntry: entry, wageEntry: null, moved: 0 };
  const otherChanges = entry.changes.filter((c) => !isWageChange(c));
  const personEntry = {
    ...entry,
    changes: [
      ...otherChanges,
      // Named negatively: this entry says a wage changed, not what it changed to.
      { field: 'wage', oldValue: '', newValue: 'Wage settings changed (see wage history)' },
    ],
  };
  const wageEntry = { ...entry, changes: wageChanges };
  return { personEntry, wageEntry, moved: wageChanges.length };
}

function historyEntryCarriesWage(entry) {
  return !!entry && Array.isArray(entry.changes) && entry.changes.some(isWageChange);
}

// ── S1b — ONE composer: a wage document from a wage profile ────────────────
//
// Until S1b two clients each kept their own copy of "the fields a profile
// puts on a person": `ViewPeople.vue :: handleApplyProfile` (the per-person
// Apply) carried the award code, stream and classification; `People.vue`'s
// bulk-apply body did not — so every bulk-applied person read "profile
// applied, not validated — no Award code set on the wage profile" and lost
// the Award-minimum check (a dev person, masked, 2026-10-30). Both paths now
// call THIS, and so does the server (`functions/bulkWageApply.js ::
// prepareBulkWageApply`), which composes from the profile DOCUMENT it loads
// rather than from anything the client sent.
//
// `orgAwardCode` is REQUIRED: the org-aware default the profile falls back
// to (`defaultAwardCode` on the organisation, else the product default the
// caller resolves). A missing one is refused, not guessed.
const deepCopy = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

function composeWageFromProfile({ profile, profileId, orgAwardCode, effectiveDate }) {
  if (!profile || typeof profile !== 'object') throw new Error('composeWageFromProfile: a profile document is required');
  if (typeof orgAwardCode !== 'string' || orgAwardCode.trim() === '') throw new Error('composeWageFromProfile: orgAwardCode is required (the organisation\'s default award code)');
  const payRateType = profile.payRateType || 'hourly';
  const wageConfig = {
    payRateType,
    employmentType: profile.employmentType || '',
    casualLoading: profile.casualLoading || 0,
    baseRate: profile.baseRate || 0,
    rules: profile.rules ? deepCopy(profile.rules) : [],
    allowances: profile.allowances ? deepCopy(profile.allowances) : [],
    awardCode: profile.awardCode || orgAwardCode,
    stream: profile.stream || '',
    classificationLevel: profile.classificationLevel || '',
  };
  if (payRateType === 'salary') {
    wageConfig.salaryPeriod = profile.salaryPeriod || 'annual';
    wageConfig.salaryAmount = profile.salaryAmount || 0;
  }
  if (payRateType === 'rates-per-day') {
    wageConfig.ratesPerDay = profile.ratesPerDay ? deepCopy(profile.ratesPerDay) : {};
  }
  return {
    wageConfig,
    appliedWageProfileId: profileId || profile.id || null,
    appliedWageProfileName: profile.name || null,
    ...(effectiveDate !== undefined ? { wageEffectiveDate: effectiveDate } : {}),
  };
}

module.exports = {
  composeWageFromProfile,
  WAGE_FIELDS,
  PAY_KEYS,
  PAY_TIERS,
  WAGE_DOC_ID,
  PRIVATE_SUBCOLLECTION,
  WAGE_HISTORY_SUBCOLLECTION,
  SHIFT_COSTS,
  TIMESHEET_COSTS,
  isPayTier,
  wageDocPath,
  wageHistoryPath,
  shiftCostsPath,
  timesheetCostsPath,
  splitWageFields,
  personCarriesWage,
  withWage,
  splitPayKeys,
  docCarriesPay,
  isoFromDdMmYyyy,
  dateOf,
  shiftCostDoc,
  timesheetCostDoc,
  isWageChange,
  splitHistoryEntry,
  historyEntryCarriesWage,
};
